import { useState, useEffect, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from "@/integrations/supabase/client";
import { Profile } from '../types/auth';

export const useAuthProvider = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Enhanced stability tracking
  const isInitialized = useRef(false);
  const lastTabVisible = useRef(true);
  const authCheckInProgress = useRef(false);
  const authStateChanges = useRef(0);

  const fetchProfile = async (userId: string) => {
    if (authCheckInProgress.current) {
      console.log('🔄 [AUTH] Profile fetch already in progress, skipping');
      return;
    }

    try {
      authCheckInProgress.current = true;
      console.log(`🔄 [AUTH] Fetching profile for user: ${userId}`);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
        
      if (error) {
        console.error('❌ [AUTH] Error fetching profile:', {
          error: error.message,
          code: error.code,
          userId
        });
        return;
      }
      
      console.log('✅ [AUTH] Profile fetched successfully:', !!data);
      setProfile(data);
    } catch (error) {
      console.error('❌ [AUTH] Unexpected error fetching profile:', error);
    } finally {
      authCheckInProgress.current = false;
    }
  };

  // Function to manually refresh the profile
  const refreshProfile = async () => {
    if (!user) {
      console.log('⚠️ [AUTH] Cannot refresh profile - no user');
      return;
    }
    console.log('🔄 [AUTH] Manual profile refresh requested');
    await fetchProfile(user.id);
  };

  // Enhanced tab visibility tracking
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      const wasVisible = lastTabVisible.current;
      lastTabVisible.current = isVisible;
      
      console.log('👁️ [AUTH] Tab visibility changed:', {
        wasVisible,
        isVisible,
        hasUser: !!user,
        hasSession: !!session,
        isInitialized: isInitialized.current
      });
      
      if (!wasVisible && isVisible && isInitialized.current && user) {
        // Tab became visible - check if we need to refresh
        const lastActivity = localStorage.getItem('lastAuthActivity');
        const now = Date.now();
        
        if (lastActivity && (now - parseInt(lastActivity)) > 5 * 60 * 1000) {
          console.log('🔄 [AUTH] Been away for >5min, checking session validity');
          
          // Don't automatically refresh - just log the state
          supabase.auth.getSession().then(({ data: { session: currentSession }, error }) => {
            if (error) {
              console.error('❌ [AUTH] Error checking session after tab switch:', error);
            } else {
              console.log('✅ [AUTH] Session check after tab switch:', {
                hasSession: !!currentSession,
                sessionChanged: currentSession?.access_token !== session?.access_token
              });
            }
          });
        }
      }
      
      if (isVisible) {
        localStorage.setItem('lastAuthActivity', Date.now().toString());
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, session]);

  // Enhanced session refresh function
  const refreshSession = async () => {
    if (authCheckInProgress.current) {
      console.log('🔄 [AUTH] Session refresh already in progress, skipping');
      return;
    }

    try {
      authCheckInProgress.current = true;
      console.log('🔄 [AUTH] Manual session refresh requested');
      
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('❌ [AUTH] Error refreshing session:', {
          error: error.message,
          code: error.code
        });
        return;
      }
      
      if (data.session) {
        console.log('✅ [AUTH] Session refreshed successfully');
        setSession(data.session);
        setUser(data.session.user);
        localStorage.setItem('lastAuthActivity', Date.now().toString());
      }
    } catch (error) {
      console.error('❌ [AUTH] Unexpected error refreshing session:', error);
    } finally {
      authCheckInProgress.current = false;
    }
  };

  // Enhanced auth state setup with better error handling
  useEffect(() => {
    let mounted = true;
    
    console.log('🔄 [AUTH] Setting up enhanced auth listener');
    
    // Set up the auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) {
          console.log('🔄 [AUTH] Component unmounted, ignoring auth change');
          return;
        }
        
        authStateChanges.current += 1;
        const changeNumber = authStateChanges.current;
        
        console.log(`🔄 [AUTH] Auth state change #${changeNumber}:`, {
          event,
          hasSession: !!newSession,
          userId: newSession?.user?.id || 'none',
          isVisible: !document.hidden
        });
        
        // Update state synchronously
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
        isInitialized.current = true;
        
        // Update last activity
        if (newSession) {
          localStorage.setItem('lastAuthActivity', Date.now().toString());
          
          // Fetch profile with a small delay to prevent auth deadlocks
          setTimeout(() => {
            if (mounted && newSession.user) {
              fetchProfile(newSession.user.id);
            }
          }, 100);
        } else {
          setProfile(null);
        }
      }
    );

    // Check for existing session with enhanced error handling
    supabase.auth.getSession().then(({ data: { session: existingSession }, error }) => {
      if (!mounted) {
        console.log('🔄 [AUTH] Component unmounted during initial session check');
        return;
      }
      
      if (error) {
        console.error('❌ [AUTH] Error getting initial session:', {
          error: error.message,
          code: error.code
        });
      } else {
        console.log('🔄 [AUTH] Initial session check completed:', {
          hasSession: !!existingSession,
          userId: existingSession?.user?.id || 'none'
        });
      }
      
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setLoading(false);
      isInitialized.current = true;
      
      if (existingSession) {
        localStorage.setItem('lastAuthActivity', Date.now().toString());
      }
    }).catch((error) => {
      console.error('🚨 [AUTH] Critical error getting initial session:', error);
      setLoading(false);
      isInitialized.current = true;
    });

    return () => {
      console.log('🔄 [AUTH] Cleaning up auth provider');
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // Remove session dependency to prevent loops

  const signOut = async () => {
    try {
      setLoading(true);
      console.log('🔄 [AUTH] Sign out requested');
      
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      console.log('✅ [AUTH] Sign out successful');
      setUser(null);
      setProfile(null);
      setSession(null);
      localStorage.removeItem('lastAuthActivity');
    } catch (error) {
      console.error('❌ [AUTH] Error signing out:', error);
    } finally {
      setLoading(false);
    }
  };

  const forceSignOut = async () => {
    try {
      console.log("🔄 [AUTH] Force sign out for password reset flow");
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ [AUTH] Error in forceSignOut:', error);
        throw error;
      }
      
      // Clear all auth state immediately
      setUser(null);
      setProfile(null);
      setSession(null);
      localStorage.removeItem('lastAuthActivity');
      
      console.log("✅ [AUTH] Force sign out completed successfully");
    } catch (error) {
      console.error('❌ [AUTH] Error force signing out:', error);
    }
  };

  return {
    user,
    profile,
    session,
    loading,
    signOut,
    forceSignOut,
    refreshSession,
    refreshProfile
  };
};
